import { PortalStore } from 'lib';
import { renderComponent } from 'utils/RenderingUtils';
import { getCurrentPageId, useFolderStore } from './FolderStore';
import { IPortalProps } from './Portal';
import { IHelpCenterOptions } from '../../components/HelpCenter/HelpCenter';
import { getFolderId } from './PortalStore';
import { getLocationPartsFromUrl } from 'components/Router/RouterUtils';
import { history } from 'components/Router';
import { FolderViewData } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder';

export { usePortalStore as PortalStore, FOLDER_CHANGED_EVENT_ID } from './PortalStore';
export { useNotificationStore as NotificationStore } from './NotificationStore';
export { getHelpCenterStore } from '../../components/HelpCenter/HelpCenterStore';
export { getLocationPartsFromUrl, getVisibleFolders } from 'components/Router/RouterUtils';
export { subscribeToFolderViewData } from './FolderStore';

export async function renderPortal(props: IPortalProps, host: HTMLElement, callback?: () => void) {
  // make sure our router stuff is loaded, since PortalStore will use it for navigation
  const { Router } = await import('components/Router'); // but didn't we already load it sync above?
  const { Portal } = await import(/* webpackChunkName: "dp-portal" */ './Portal');
  const { baseUrl, isStudio } = props.loadModel.Options;

  return renderComponent({
    container: host,
    // baseName here should be whatever the portal/studio path _IS_ after whatever redirecting MVC already did

    component: (
      <Router portalBaseUrl={baseUrl} isStudio={isStudio}>
        <Portal {...props} />
      </Router>
    ),
    callback,
    // Note: it is cannot be unmounted actually, because it is the root of the app
    autoUnmount: false,
  });
}

export async function renderHelpCenter(options: IHelpCenterOptions, container: HTMLElement, callback?: () => void) {
  const { HelpCenter } = await import('components/HelpCenter/HelpCenter');

  return renderComponent({
    container,
    component: <HelpCenter {...options} newStyle />,
    callback,
    autoUnmount: true,
  });
}

export function getCurrentFolderState() {
  // get folderId from URL if the PortalStore isn't initialized, etc.
  const folderId = getFolderId(PortalStore.getState()) || getCurrentFolderIdFromUrl();
  return useFolderStore.getState().folders.get(folderId);
}

export async function getFolderViewData(folderId?: string) {
  const { folderStoreActions } = useFolderStore.getState();
  return await folderStoreActions.getFolderViewData(folderId);
}

/**
 * This is for use in legacy, non-promise code, that does not expect the callback to fire if `folderId` is falsy.
 *
 * If you are writing something new, just use `getFolderViewData` directly
 *
 * @param folderId to fetch
 * @param callback to trigger if Folder is found,
 * @return FolderViewData or null
 * @deprecated this is just for legacy support, not new stuff.
 */
export async function getFolderViewDataForLegacy(folderId?: string, callback?: (fvd: FolderViewData | null) => void) {
  const { folderStoreActions } = useFolderStore.getState();
  return folderStoreActions.getFolderViewDataForLegacy(folderId, callback);
}

export function getFolderStorage() {
  return useFolderStore.getState();
}

export function getCurrentFolderIdFromUrl() {
  return getLocationPartsFromUrl(history.location).folderId || '';
}

export function getCurrentPageNameFromUrl() {
  return getLocationPartsFromUrl(history.location).pageName;
}

export function getCurrentPageIdForActiveFolder() {
  return getCurrentPageId(useFolderStore.getState());
}

export function showFolderInLayer(folderId: string) {
  PortalStore.getState().actions.showFolderInLayer(folderId);
}

export function closeFolderLayer(folderId: string) {
  PortalStore.getState().actions.closeFolderLayer(folderId);
}
